// Minimal YAML-ish frontmatter parser sufficient for our help articles.
// Supports: scalar strings, numbers, booleans, and inline arrays [a, b, c].
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  if (!match) return { data: {}, content: raw };
  const [, header, body] = match;
  const data: Record<string, unknown> = {};
  for (const line of header.split(/\r?\n/)) {
    const m = /^([A-Za-z0-9_-]+)\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    const [, key, rawValue] = m;
    let value: unknown = rawValue.trim();
    if (typeof value === "string") {
      if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      } else if (value === "true") value = true;
      else if (value === "false") value = false;
      else if (value !== "" && !Number.isNaN(Number(value)) && /^-?\d+(\.\d+)?$/.test(value as string)) {
        value = Number(value);
      } else if ((value as string).startsWith("[") && (value as string).endsWith("]")) {
        value = (value as string)
          .slice(1, -1)
          .split(",")
          .map((s) => s.trim().replace(/^['"]|['"]$/g, ""))
          .filter(Boolean);
      }
    }
    data[key] = value;
  }
  return { data, content: body };
}

export type HelpSection =
  | "getting-started"
  | "my-library"
  | "my-excerpts"
  | "readwise-highlights"
  | "my-feed-reader"
  | "my-podcasts"
  | "notes"
  | "composer"
  | "image-studio"
  | "publishing"
  | "citations-bibliography"
  | "workspaces-members"
  | "agency"
  | "billing-credits"
  | "affiliate-program"
  | "integrations"
  | "settings-reference"
  | "security-privacy"
  | "account-lifecycle"
  | "developer"
  | "troubleshooting-faq";

export type HelpArticle = {
  slug: string;
  title: string;
  summary: string;
  section: HelpSection;
  sectionLabel: string;
  order: number;
  tags: string[];
  body: string;

};

export const HELP_SECTION_LABELS: Record<HelpSection, string> = {
  "getting-started": "Getting started",
  "my-library": "My Library",
  "my-excerpts": "My Excerpts",
  "readwise-highlights": "Readwise Highlights",
  "my-feed-reader": "My Feed Reader",
  "my-podcasts": "My Podcasts",
  notes: "Notes",
  composer: "Composer",
  "image-studio": "Image Studio",
  publishing: "Publishing",
  "citations-bibliography": "Citations & bibliography",
  "workspaces-members": "Workspaces, members & roles",
  agency: "Agency plan",
  "billing-credits": "Billing & credits",
  "affiliate-program": "Affiliate program",
  integrations: "Integrations",
  "settings-reference": "Settings reference",
  "security-privacy": "Security & privacy",
  "account-lifecycle": "Account lifecycle",
  developer: "Developer & API",
  "troubleshooting-faq": "Troubleshooting & FAQ",
};

export const HELP_SECTION_ORDER: HelpSection[] = [
  "getting-started",
  "my-library",
  "my-excerpts",
  "readwise-highlights",
  "my-feed-reader",
  "my-podcasts",
  "notes",
  "composer",
  "image-studio",
  "publishing",
  "citations-bibliography",
  "workspaces-members",
  "agency",
  "billing-credits",
  "affiliate-program",
  "integrations",
  "settings-reference",
  "security-privacy",
  "account-lifecycle",
  "developer",
  "troubleshooting-faq",
];

// Eagerly load every markdown file under src/content/help/**/*.md as raw strings.
const modules = import.meta.glob("/src/content/help/**/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

function parseArticle(path: string, raw: string): HelpArticle | null {
  const parsed = parseFrontmatter(raw);
  const data = parsed.data as Partial<HelpArticle> & { section?: string };
  if (!data.slug || !data.title || !data.section) {
    console.warn(`[help] Skipping ${path}: missing slug/title/section`);
    return null;
  }
  const section = data.section as HelpSection;
  if (!HELP_SECTION_LABELS[section]) {
    console.warn(`[help] Skipping ${path}: unknown section "${section}"`);
    return null;
  }
  return {
    slug: data.slug,
    title: data.title,
    summary: data.summary ?? "",
    section,
    sectionLabel: HELP_SECTION_LABELS[section],
    order: typeof data.order === "number" ? data.order : 999,
    tags: Array.isArray(data.tags) ? data.tags : [],
    body: parsed.content.trim(),
  };
}

export const HELP_ARTICLES: HelpArticle[] = Object.entries(modules)
  .map(([path, raw]) => parseArticle(path, raw))
  .filter((a): a is HelpArticle => a !== null)
  .sort((a, b) => {
    const sa = HELP_SECTION_ORDER.indexOf(a.section);
    const sb = HELP_SECTION_ORDER.indexOf(b.section);
    if (sa !== sb) return sa - sb;
    if (a.order !== b.order) return a.order - b.order;
    return a.title.localeCompare(b.title);
  });

export const HELP_ARTICLES_BY_SLUG: Record<string, HelpArticle> = Object.fromEntries(
  HELP_ARTICLES.map((a) => [a.slug, a]),
);

export function getArticlesBySection(): Array<{ section: HelpSection; label: string; articles: HelpArticle[] }> {
  const grouped = new Map<HelpSection, HelpArticle[]>();
  for (const article of HELP_ARTICLES) {
    const list = grouped.get(article.section) ?? [];
    list.push(article);
    grouped.set(article.section, list);
  }
  return HELP_SECTION_ORDER.filter((s) => grouped.has(s)).map((section) => ({
    section,
    label: HELP_SECTION_LABELS[section],
    articles: grouped.get(section)!,
  }));
}

export function searchArticles(query: string): HelpArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return HELP_ARTICLES.filter((a) => {
    const hay = [a.title, a.summary, a.tags.join(" "), a.body].join(" ").toLowerCase();
    return hay.includes(q);
  }).slice(0, 30);
}
